import React, { useState } from 'react';
import { Wallet, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import { createDeposit, type DepositRow } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

type PaymentMode = 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'other';

interface RecordDepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  clinicId: string;
  /** With what was taken; `print` when saved with "Save & print" */
  onRecorded: (deposit: { amount: number; payment_mode: string; reference_number?: string; receipt_number?: string | null }, print: boolean) => void;
}

export function RecordDepositModal({
                                      isOpen,
                                      onClose,
                                      patientId,
                                      clinicId,
                                      onRecorded,
                                    }: RecordDepositModalProps) {
  const [amount, setAmount]                   = useState(0);
  const [paymentMode, setPaymentMode]         = useState<PaymentMode>('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [upiApp, setUpiApp]                   = useState('');
  const [bankName, setBankName]               = useState('');
  const [loading, setLoading]                 = useState(false);
  const { formatCurrency } = useBillingUtils();

  const handleClose = () => {
    setAmount(0);
    setPaymentMode('cash');
    setReferenceNumber('');
    setUpiApp('');
    setBankName('');
    onClose();
  };

  const handleModeChange = (mode: string) => {
    setPaymentMode(mode as PaymentMode);
    setReferenceNumber('');
    setUpiApp('');
    setBankName('');
  };

  const handleSubmit = async (print = false) => {
    if (amount <= 0) return;
    setLoading(true);
    try {
      const deposit: DepositRow = {
        amount,
        payment_mode:     paymentMode,
        reference_number: referenceNumber || undefined,
        upi_app:          upiApp          || undefined,
        bank_name:        bankName        || undefined,
      };
      const result = await createDeposit(patientId, clinicId, deposit);
      toast({
        title: 'Deposit recorded',
        description: `${formatCurrency(amount)} added. Available advance: ${formatCurrency(result.available_advance)}`,
      });
      handleClose();
      onRecorded({ amount, payment_mode: paymentMode, reference_number: referenceNumber || undefined, receipt_number: result?.deposit?.receipt_number }, print);
    } catch (err: any) {
      toast({ title: 'Failed to record deposit', description: err.message || 'Could not record deposit', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              Record Advance Deposit
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Amount *</label>
              <div className="relative">
                <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                    placeholder="Enter amount"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Payment Method</label>
              <Select value={paymentMode} onValueChange={handleModeChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="netbanking">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="other">Insurance / Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {paymentMode === 'upi' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">UPI App</label>
                    <Select value={upiApp} onValueChange={setUpiApp}>
                      <SelectTrigger><SelectValue placeholder="Select app (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gpay">Google Pay</SelectItem>
                        <SelectItem value="phonepe">PhonePe</SelectItem>
                        <SelectItem value="paytm">Paytm</SelectItem>
                        <SelectItem value="bhim">BHIM</SelectItem>
                        <SelectItem value="other">Other UPI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">UPI Transaction ID</label>
                    <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                           placeholder="e.g. 123456789012" className="font-mono text-sm" />
                  </div>
                </div>
            )}

            {paymentMode === 'cheque' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Cheque Number</label>
                    <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                           placeholder="Enter cheque number" className="font-mono text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Bank Name</label>
                    <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. SBI, HDFC, ICICI" />
                  </div>
                </div>
            )}

            {paymentMode === 'netbanking' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Transaction Reference</label>
                  <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                         placeholder="NEFT/RTGS reference number" className="font-mono text-sm" />
                </div>
            )}

            {paymentMode === 'card' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Card Last 4 Digits</label>
                  <Input value={referenceNumber}
                         onChange={(e) => setReferenceNumber(e.target.value.replace(/\D/g, '').slice(0, 4))}
                         placeholder="e.g. 4321" maxLength={4} className="font-mono text-sm w-24" />
                </div>
            )}
          </div>

          <div data-kb-footer className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="ghost" onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button variant="outline" onClick={() => handleSubmit(false)} disabled={loading || amount <= 0}>Save</Button>
            {/* The patient is at the desk — their receipt is the usual next step, as "Save & print" on the prescription pad */}
            <Button onClick={() => handleSubmit(true)} disabled={loading || amount <= 0}>
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save & print'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
}
